// client/src/v2/debug.tsx
//
// Sheet de Diagnostico do tester. Acessivel por qualquer user via tap
// secreto (7 toques no header de Perfil em 4s).

import React from 'react';
import { Sheet } from './common';
import { Icon } from './icons';
import { getRecentLogs, getSessionId, flushNow, maskEmail, maskId, tlog } from './telemetry';

export const DebugSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  profile?: { id?: string; email?: string; plan?: string; role?: string; display_name?: string } | null;
}> = ({ open, onClose, profile }) => {
  const [snapshot, setSnapshot] = React.useState<any>(null);
  const [reportCode, setReportCode] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const collect = React.useCallback(async () => {
    const cap: any = (typeof window !== 'undefined') ? (window as any).Capacitor : null;
    const platform: string = cap?.getPlatform?.() || 'web';
    const isNative = !!cap?.isNativePlatform?.();
    const buildVersion = document.querySelector('meta[name="build-version"]')?.getAttribute('content') || 'N/A';

    let nativeInfo: any = null;
    let pushPerm: any = null;
    let localPerm: any = null;
    let cameraPerm: any = null;

    if (isNative) {
      try {
        const mod: any = await import('@capacitor/app');
        const info = await mod.App.getInfo();
        nativeInfo = { name: info.name, id: info.id, version: info.version, build: info.build };
      } catch (e: any) { nativeInfo = { error: e?.message || String(e) }; }

      try {
        const m: any = await import('@capacitor/push-notifications');
        if (m.PushNotifications?.checkPermissions) pushPerm = await m.PushNotifications.checkPermissions();
      } catch (e: any) { pushPerm = { error: e?.message }; }

      try {
        const m: any = await import('@capacitor/local-notifications');
        if (m.LocalNotifications?.checkPermissions) localPerm = await m.LocalNotifications.checkPermissions();
      } catch (e: any) { localPerm = { error: e?.message }; }

      try {
        const m: any = await import('@capacitor-mlkit/barcode-scanning');
        if (m.BarcodeScanner?.checkPermissions) cameraPerm = await m.BarcodeScanner.checkPermissions();
      } catch (e: any) { cameraPerm = { error: e?.message }; }
    }

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const langs = typeof navigator !== 'undefined' ? (navigator.languages || [navigator.language]) : [];

    return {
      session: getSessionId(),
      time: new Date().toISOString(),
      buildVersion,
      platform,
      isNative,
      nativeInfo,
      user: profile ? {
        id: maskId(profile.id),
        email: maskEmail(profile.email),
        plan: profile.plan || null,
        role: profile.role || null,
        displayName: profile.display_name || null,
      } : null,
      permissions: { push: pushPerm, local: localPerm, camera: cameraPerm },
      viewport: {
        w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio,
      },
      navigator: { ua, langs, online: navigator.onLine },
      url: window.location.href,
      logs: getRecentLogs(),
    };
  }, [profile]);

  React.useEffect(() => {
    if (!open) return;
    setReportCode(null); setSendError(null); setCopied(false);
    collect().then(setSnapshot);
    tlog('debug:opened', { session: getSessionId() });
  }, [open, collect]);

  const copyAll = async () => {
    if (!snapshot) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setSendError('Falha ao copiar. Tente Enviar pro suporte.');
    }
  };

  const sendReport = async () => {
    if (!snapshot) return;
    setSending(true); setSendError(null);
    try {
      flushNow();
      const res = await fetch('/api/debug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          snapshot,
          userId: profile?.id || null,
        }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const j = await res.json();
      if (!j?.code) throw new Error('Resposta invalida');
      setReportCode(j.code);
      tlog('debug:report_sent', { code: j.code });
    } catch (e: any) {
      setSendError(e?.message || 'Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            Suporte tecnico
          </div>
          <div className="serif" style={{ fontSize: 30, lineHeight: 1.05 }}>Diagnostico</div>
        </div>
        <button onClick={onClose} style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Icon name="close" size={16}/></button>
      </div>

      <div style={{ marginTop: 18, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Tela tecnica usada quando voce reportar um problema. Toque em <b>Enviar pro suporte</b>:
        vai gerar um codigo curto. Cole esse codigo no email pra contato@forjamente.com.br
        que a gente puxa todos os detalhes aqui.
      </div>

      {reportCode && (
        <div style={{
          marginTop: 18, padding: 18, borderRadius: 14,
          background: 'var(--ember-soft)', color: '#fff', textAlign: 'center',
        }}>
          <div style={{ fontSize: 12, opacity: 0.9, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Seu codigo de reporte
          </div>
          <div className="serif" style={{ fontSize: 42, fontWeight: 600, letterSpacing: '0.08em', marginTop: 6 }}>
            #{reportCode}
          </div>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 8 }}>
            Inclua esse codigo no email pra acelerar o atendimento.
          </div>
        </div>
      )}

      {!reportCode && (
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button onClick={copyAll} style={{
            padding: '14px', borderRadius: 14,
            background: 'var(--paper-2)', color: 'var(--ink-2)',
            fontSize: 14, fontWeight: 500, border: '1px solid var(--line)',
          }}>{copied ? 'Copiado!' : 'Copiar tudo'}</button>
          <button onClick={sendReport} disabled={sending} style={{
            padding: '14px', borderRadius: 14,
            background: sending ? 'var(--paper-3)' : 'var(--ink)', color: 'var(--paper)',
            fontSize: 14, fontWeight: 500,
            opacity: sending ? 0.7 : 1,
          }}>{sending ? 'Enviando...' : 'Enviar pro suporte'}</button>
        </div>
      )}

      {sendError && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 12,
          background: 'rgba(192,58,42,0.12)', color: '#C03A2A',
          fontSize: 13, textAlign: 'center',
        }}>{sendError}</div>
      )}

      {snapshot && (
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--ink-3)' }}>
            Detalhes tecnicos ({snapshot.logs?.length || 0} logs)
          </summary>
          <pre style={{
            marginTop: 10, padding: 12, borderRadius: 10,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            fontSize: 10.5, lineHeight: 1.45, overflow: 'auto',
            maxHeight: 360, fontFamily: 'Geist Mono, ui-monospace, monospace',
            color: 'var(--ink-2)',
          }}>{JSON.stringify(snapshot, null, 2)}</pre>
        </details>
      )}
    </Sheet>
  );
};
